import type { Triangle, Vec3, Polygon, Vec2 } from './types';

/** Parse binary or ASCII STL. Returns triangles in mm units (whatever the STL encodes). */
export function parseStl(buffer: ArrayBuffer): Triangle[] {
  // Detect ASCII vs binary. ASCII starts with "solid " followed by printable chars; binary
  // has an 80-byte header that often starts with "solid" too, so we additionally check the
  // expected file size: 80 + 4 + 50 * triCount.
  const view = new DataView(buffer);
  const triCount = buffer.byteLength >= 84 ? view.getUint32(80, true) : 0;
  const expectedBinarySize = 80 + 4 + 50 * triCount;
  const looksBinary = buffer.byteLength === expectedBinarySize;

  if (looksBinary) return parseStlBinary(buffer);

  // Try ASCII
  try {
    const text = new TextDecoder().decode(buffer);
    if (text.trimStart().toLowerCase().startsWith('solid')) return parseStlAscii(text);
  } catch {
    // fallthrough
  }
  // Fall back to binary attempt anyway
  return parseStlBinary(buffer);
}

function parseStlBinary(buffer: ArrayBuffer): Triangle[] {
  const view = new DataView(buffer);
  const triCount = view.getUint32(80, true);
  const tris: Triangle[] = new Array(triCount);
  let off = 84;
  for (let i = 0; i < triCount; i++) {
    const nx = view.getFloat32(off, true);
    const ny = view.getFloat32(off + 4, true);
    const nz = view.getFloat32(off + 8, true);
    const ax = view.getFloat32(off + 12, true);
    const ay = view.getFloat32(off + 16, true);
    const az = view.getFloat32(off + 20, true);
    const bx = view.getFloat32(off + 24, true);
    const by = view.getFloat32(off + 28, true);
    const bz = view.getFloat32(off + 32, true);
    const cx = view.getFloat32(off + 36, true);
    const cy = view.getFloat32(off + 40, true);
    const cz = view.getFloat32(off + 44, true);
    tris[i] = {
      a: [ax, ay, az],
      b: [bx, by, bz],
      c: [cx, cy, cz],
      normal: [nx, ny, nz]
    };
    off += 50;
  }
  return tris;
}

function parseStlAscii(text: string): Triangle[] {
  const tris: Triangle[] = [];
  // Permissive regex over the whole file.
  const re =
    /facet\s+normal\s+([-\d.eE+]+)\s+([-\d.eE+]+)\s+([-\d.eE+]+)\s+outer\s+loop\s+vertex\s+([-\d.eE+]+)\s+([-\d.eE+]+)\s+([-\d.eE+]+)\s+vertex\s+([-\d.eE+]+)\s+([-\d.eE+]+)\s+([-\d.eE+]+)\s+vertex\s+([-\d.eE+]+)\s+([-\d.eE+]+)\s+([-\d.eE+]+)\s+endloop\s+endfacet/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    tris.push({
      normal: [parseFloat(m[1]), parseFloat(m[2]), parseFloat(m[3])],
      a: [parseFloat(m[4]), parseFloat(m[5]), parseFloat(m[6])],
      b: [parseFloat(m[7]), parseFloat(m[8]), parseFloat(m[9])],
      c: [parseFloat(m[10]), parseFloat(m[11]), parseFloat(m[12])]
    });
  }
  return tris;
}

export function meshBounds(tris: Triangle[]): { min: Vec3; max: Vec3 } {
  if (tris.length === 0) {
    return { min: [0, 0, 0], max: [0, 0, 0] };
  }
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (const t of tris) {
    for (const v of [t.a, t.b, t.c]) {
      if (v[0] < minX) minX = v[0];
      if (v[1] < minY) minY = v[1];
      if (v[2] < minZ) minZ = v[2];
      if (v[0] > maxX) maxX = v[0];
      if (v[1] > maxY) maxY = v[1];
      if (v[2] > maxZ) maxZ = v[2];
    }
  }
  return { min: [minX, minY, minZ], max: [maxX, maxY, maxZ] };
}

/** Translate triangles so that (cx, cy, minZ) sits on the build-plate origin. */
export function centerOnPlate(tris: Triangle[]): Triangle[] {
  const { min, max } = meshBounds(tris);
  const dx = -(min[0] + max[0]) / 2;
  const dy = -(min[1] + max[1]) / 2;
  const dz = -min[2];
  return tris.map((t) => ({
    normal: t.normal,
    a: [t.a[0] + dx, t.a[1] + dy, t.a[2] + dz],
    b: [t.b[0] + dx, t.b[1] + dy, t.b[2] + dz],
    c: [t.c[0] + dx, t.c[1] + dy, t.c[2] + dz]
  }));
}

// ---------- Polygon helpers (2D) ----------

export function signedArea(poly: Polygon): number {
  let a = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    a += (poly[j][0] + poly[i][0]) * (poly[j][1] - poly[i][1]);
  }
  return a * 0.5; // negative -> CCW in standard math axes (y-up); positive -> CW
}

/** Return CCW (outer-loop) winding regardless of input. */
export function ensureCCW(poly: Polygon): Polygon {
  return signedArea(poly) > 0 ? [...poly].reverse() : poly;
}

export function polyBounds(poly: Polygon): { min: Vec2; max: Vec2 } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of poly) {
    if (p[0] < minX) minX = p[0];
    if (p[1] < minY) minY = p[1];
    if (p[0] > maxX) maxX = p[0];
    if (p[1] > maxY) maxY = p[1];
  }
  return { min: [minX, minY], max: [maxX, maxY] };
}

export function pointInPolygon(p: Vec2, poly: Polygon): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0], yi = poly[i][1];
    const xj = poly[j][0], yj = poly[j][1];
    const intersect =
      yi > p[1] !== yj > p[1] &&
      p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi + 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function polyLength(poly: Polygon, closed = true): number {
  let len = 0;
  for (let i = 0; i < poly.length - 1; i++) {
    len += Math.hypot(poly[i + 1][0] - poly[i][0], poly[i + 1][1] - poly[i][1]);
  }
  if (closed && poly.length > 1) {
    len += Math.hypot(poly[0][0] - poly[poly.length - 1][0], poly[0][1] - poly[poly.length - 1][1]);
  }
  return len;
}

/** Linear interpolation along a closed polygon. t in [0, 1). Returns a point. */
export function sampleClosedPoly(poly: Polygon, t: number): Vec2 {
  if (poly.length < 2) return poly[0] ?? [0, 0];
  const total = polyLength(poly, true);
  let target = ((t % 1) + 1) % 1 * total;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    const seg = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (target <= seg) {
      const u = seg > 1e-9 ? target / seg : 0;
      return [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u];
    }
    target -= seg;
  }
  return poly[0];
}
